import type { NegotiationRoundDto, SourcingEventDto } from "../../../autogen/bff-api";
import { allowsPartIIDialogue } from "./partIIDialogue";

export interface RoundInviteCandidate {
  organizationId: string;
  personIds: string[];
  recipientLabels: string[];
}

/**
 * The organizations a round may invite, mirroring eligibleRoundParticipants in the BFF. Private
 * competitions list the organizations invited to the event, each with the people the buyer picked.
 * A public two-stage competition lists the suppliers frozen into the § 23-2 invitation — it names
 * organizations, not people, so the recipients are the portal accounts behind them, which the
 * server fills in when the invite is created. A Part II dialogue (FOA § 9-3) is held with the
 * suppliers who tendered, in a restricted competition too.
 */
export const roundInviteCandidates = (
  event: SourcingEventDto,
  round: NegotiationRoundDto,
  tendererOrganizationIds: string[] = [],
): RoundInviteCandidate[] => {
  const ctx = event.eventContext;
  if (ctx.type === "Private") {
    return ctx.organizationInvites.map((invite) => ({
      organizationId: invite.organizationId,
      personIds: (invite.persons ?? []).map((person) => person.id),
      recipientLabels: [
        ...(invite.persons ?? []).map(
          (person) => [person.firstName, person.lastName].filter(Boolean).join(" ") || person.email || person.id,
        ),
        ...(invite.emailInvites ?? []).map((emailInvite) => emailInvite.email),
      ],
    }));
  }
  if (ctx.type !== "Public") return [];
  const participants = allowsPartIIDialogue(event)
    ? [...new Set([...tendererOrganizationIds, ...round.invites.map((invite) => invite.organizationId)])]
    : (ctx.qualificationRound?.invitation?.invitedOrganizationIds ?? []);
  return participants.map((organizationId) => {
    const roundInvite = round.invites.find((invite) => invite.organizationId === organizationId);
    return {
      organizationId,
      personIds: (roundInvite?.persons ?? []).map((person) => person.id),
      recipientLabels: (roundInvite?.persons ?? []).map((person) => person.name),
    };
  });
};
